import { useCallback, useEffect, useMemo, useState } from 'react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { wafFetch } from '@v2/shared/lib/wafFetch';
import { Magazine, MagazinePage } from './types';

const ENDPOINT = '/services_api/storefront/v1/third_party/ZinioUnlimited/products.json';

/** Long enough that typing a word is one request rather than five. */
const SEARCH_DEBOUNCE_MS = 400;

/** What the visitor has asked for. One object, so a change to it is always atomic. */
type Request = {
  categoryId: string | null;
  search: string;
  page: number;
};

/** The request minus its page — what makes one list a different list from another. */
function listKey(request: Request): string {
  return `${request.categoryId ?? ''}|${request.search}`;
}

function buildUrl(base: string, request: Request): string {
  const params = new URLSearchParams({ page: String(request.page) });

  // `!= null`, loosely, which is the one place in here that is deliberate: it excludes
  // both null and undefined while keeping "0" and "", because these ids are the third
  // party's and are never interpreted — an id of "0" is a category like any other, and
  // truthiness would drop it and quietly answer with the unfiltered list instead.
  // eslint-disable-next-line eqeqeq
  if (request.categoryId != null) {
    params.set('category', request.categoryId);
  }

  if (request.search !== '') {
    params.set('search_term', request.search);
  }

  return `${base}?${params.toString()}`;
}

type Loaded = {
  key: string;
  /** By page number, one-based minus one — not appended, so a repeat replaces. */
  pages: Magazine[][];
  /** The highest page that came back, and whether it filled the page size. */
  lastPage: number;
  lastPageWasFull: boolean;
};

/**
 * The magazines, and every way the visitor has of changing which ones.
 *
 * All of it happens in the browser, as it did at the source: the sidebar and the heading
 * are the server's, the grid is not. What the server does render is the page a search
 * engine sees, and there is nothing here worth indexing — thousands of covers, none of
 * which we sell.
 *
 * Category, search and page live in ONE piece of state rather than three. That is not
 * tidiness: picking a category has to reset the page, and with the page held separately
 * the reset lands a render later than the fetch does — so switching category while on
 * page 3 fires a request for page 3 of the new category and then aborts it. Changing them
 * together makes that unrepresentable.
 *
 * This is Magzter Gold's hook with one difference, and it is the third party's difference
 * rather than a choice: **Zinio reports no count of its own.** Its response is a bare
 * list, and asking past the end answers an empty one. So the end of the catalogue is a
 * page that came back shorter than the size that was asked for, which is what `hasMore`
 * reads — see the note there. Everything else, including holding pages by number so two
 * requests over the same page cannot double up, is the same.
 */
export default function useMagazines() {
  const { buildUrl: absolute } = useUrlGenerator();
  const endpoint = absolute(ENDPOINT);

  const [request, setRequest] = useState<Request>({ categoryId: null, search: '', page: 1 });
  // What the box says, which leads `request.search` by the debounce.
  const [searchInput, setSearchInput] = useState('');

  const [loaded, setLoaded] = useState<Loaded>({
    key: listKey({ categoryId: null, search: '', page: 1 }),
    pages: [],
    lastPage: 0,
    lastPageWasFull: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setRequest((current) =>
        current.search === searchInput ? current : { ...current, search: searchInput, page: 1 }
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const key = listKey(request);
    const controller = new AbortController();

    setIsLoading(true);
    setHasFailed(false);

    wafFetch(buildUrl(endpoint, request), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Magazines answered ${response.status}`);
        }

        return response.json() as Promise<MagazinePage>;
      })
      .then((answer) => {
        setLoaded((current) => {
          // A different list than the one in state means this is the first page of a new
          // one — start it empty rather than merging into the previous category's pages.
          const base = current.key === key ? current.pages.slice() : [];
          const results = answer.results ?? [];
          base[request.page - 1] = results;

          return {
            key,
            pages: base,
            lastPage: request.page,
            // Whether the page filled up, read off `total` rather than off `results`.
            //
            // `total` counts what the third party sent; `results` is what survived
            // normalising, and a title with no cover is dropped. Testing the array would
            // make a full page of twenty look short whenever one of its titles could not
            // be drawn — scrolling would stop on that page and everything below it in the
            // catalogue would be unreachable. `total` is the running count of titles
            // known to exist, so reaching `page * limit` means the page was full.
            lastPageWasFull: answer.limit > 0 && answer.total >= request.page * answer.limit,
          };
        });
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        // An abort is this effect being superseded, not a failure — the newer request
        // owns the state now, and saying "we could not load these" over it would be a
        // lie. It is also what keeps a stale answer out of the merge above: a superseded
        // request rejects here rather than resolving there.
        if (controller.signal.aborted) {
          return;
        }

        console.error('Failed to load Zinio Unlimited magazines', error);
        setHasFailed(true);
        setIsLoading(false);
      });

    return () => controller.abort();
    // `endpoint` is derived from the site, which does not change within a page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Only the list the visitor is currently asking for. Anything still in state under a
  // different key belongs to the category they just left, and showing it under the new
  // heading for the length of one request reads as the wrong covers rather than as
  // loading.
  const isCurrent = loaded.key === listKey(request);

  // Memoised because it is not cheap and it runs on every render — and one of those
  // renders happens on every keystroke in the search box, 400ms before the debounce does
  // anything, over however many hundred covers are already loaded.
  const allPages = useMemo(
    () => loaded.pages.reduce<Magazine[]>((all, one) => all.concat(one ?? []), []),
    [loaded.pages]
  );

  const magazines = isCurrent ? allPages : [];

  return {
    magazines,
    isLoading,
    hasFailed,
    /**
     * A short page is the end of the list.
     *
     * Magzter Gold's hook counts pages against a total, because Magzter reports one.
     * Zinio does not report anything: no count, no page count, and no error at the end —
     * page 9999 of a 25-category catalogue is a plain empty list. The only thing its
     * answer says about whether more exists is whether this page filled up, so that is
     * what is read, and it is exact rather than approximate: Zinio honours the page size
     * we ask for, so a page with fewer rows on it than we asked for is the last one.
     * See `lastPageWasFull` for why that is measured off `total` and not off the covers.
     *
     * It also stops one request earlier than headless_jshop did. That version kept going
     * while a page was merely non-empty, so a catalogue ending on an exact page boundary
     * always cost one extra round trip to discover — this one only pays that when the
     * last page is genuinely full.
     */
    hasMore: isCurrent && !hasFailed && loaded.lastPageWasFull,
    loadMore: useCallback(() => {
      setRequest((current) => ({ ...current, page: current.page + 1 }));
    }, []),
    searchInput,
    setSearchInput,
    selectedCategoryId: request.categoryId,
    selectCategory: useCallback((categoryId: string | null) => {
      // Clearing the box with the category is what the sidebar did at the source: a term
      // typed against one category rarely means anything against the next, and leaving it
      // in place reads as a category that has almost nothing in it.
      setSearchInput('');
      setRequest({ categoryId, search: '', page: 1 });
    }, []),
  };
}
